import { BaseCatalogItemTransformer } from './baseCatalogItem.transformer';
import type { Nftcollection } from '@/types/nftcollection';
import type { CatalogType } from '../services/catalog/catalog-types';
import type { Collectiontype } from '@/types/collectiontype';
import type { Droptype } from '@/types/droptype';
import { getMediaData } from '@/lib/utils/media.utils';
import { getBlockchainTag, getTokenStandardTag, getYearTag, getMarketplaceTag, getCategoryTags, combineTags } from '@/lib/utils/tagGenerator';

/**
 * Extended NFT Collection with computed display fields
 */
export interface EnrichedCollection extends Nftcollection {
  tags: string[];
  cats: string[];
  isDrop: boolean;
  collectionTypes: (number | Collectiontype)[];
  dropTypes: (number | Droptype)[];
}

/**
 * Transformer for converting NFTCollection entities to CatalogItem
 */
export class NftCollectionTransformer extends BaseCatalogItemTransformer<Nftcollection> {
  // Optional additional data that can be passed during transformation
  private artworkCount?: number;
  private collectorCount?: number;

  // Method to set additional data before transformation
  setAdditionalData(data: { artworkCount?: number; collectorCount?: number }) {
    this.artworkCount = data.artworkCount;
    this.collectorCount = data.collectorCount;
    return this;
  }

  protected getId(nftCollection: Nftcollection): string | number {
    return nftCollection.id;
  }

  protected getType(_nftCollection: Nftcollection): CatalogType {
    return 'collection';
  }

  protected getSlug(nftCollection: Nftcollection): string {
    return nftCollection.slug;
  }

  protected getHref(nftCollection: Nftcollection): string {
    const isDrop = this.getIsDrop(nftCollection);
    return `/catalog/${isDrop ? 'drops' : 'collections'}/${nftCollection.slug}`;
  }

  protected getTitle(nftCollection: Nftcollection): string {
    return nftCollection.name;
  }

  protected getDisplayTitle(nftCollection: Nftcollection): string {
    return nftCollection.name;
  }

  protected getFilename(nftCollection: Nftcollection): string {
    return nftCollection.slug;
  }

  protected getThumbnail(nftCollection: Nftcollection): string {
    const cardImageData = getMediaData(nftCollection.cardImage);
    return cardImageData.url || '';
  }

  protected getImageUrl(nftCollection: Nftcollection): string {
    const cardImageData = getMediaData(nftCollection.cardImage);
    const bannerImageData = getMediaData(nftCollection.bannerImage);
    const thumbnail = cardImageData.url || '';
    return bannerImageData.url || thumbnail;
  }

  protected getWidth(nftCollection: Nftcollection): number {
    const cardImageData = getMediaData(nftCollection.cardImage);
    return cardImageData.width || 800;
  }

  protected getHeight(nftCollection: Nftcollection): number {
    const cardImageData = getMediaData(nftCollection.cardImage);
    return cardImageData.height || 600;
  }

  protected getDate(nftCollection: Nftcollection): string {
    return nftCollection.createdDate || nftCollection.createdAt;
  }

  protected getYear(nftCollection: Nftcollection): number {
    const date = nftCollection.createdDate ||
      nftCollection.startDate ||
      nftCollection.createdAt;
    return new Date(String(date)).getFullYear();
  }

  protected getDescription(nftCollection: Nftcollection): string {
    return nftCollection.description;
  }

  protected getLongDescription(nftCollection: Nftcollection): string {
    return nftCollection.description;
  }

  protected getPrice(nftCollection: Nftcollection): number {
    return nftCollection.publicPrice || 0;
  }

  protected getAvailable(nftCollection: Nftcollection): boolean {
    return nftCollection.status === 'draft';
  }

  protected getArtworkCount(nftCollection: Nftcollection): number {
    // Use provided artworkCount if available, otherwise use totalSupply
    return this.artworkCount ?? nftCollection.totalSupply ?? 0;
  }

  protected getCollectorCount(nftCollection: Nftcollection): number {
    // Use provided collectorCount if available, otherwise use uniqueHolders
    return this.collectorCount ?? nftCollection.uniqueHolders ?? 0;
  }

  protected getStartDate(nftCollection: Nftcollection): string | null {
    return nftCollection.startDate || null;
  }

  protected getEndDate(nftCollection: Nftcollection): string | null {
    return nftCollection.endDate || null;
  }

  protected getRevealDateTime(nftCollection: Nftcollection): string | null {
    return nftCollection.revealDateTime || null;
  }

  protected getPublicPrice(nftCollection: Nftcollection): number | null {
    return nftCollection.publicPrice || null;
  }

  protected getTotalSupply(nftCollection: Nftcollection): number | null {
    return nftCollection.totalSupply || null;
  }

  protected getContractAddress(nftCollection: Nftcollection): string | null {
    return nftCollection.requiredInformation?.contractAddress || null;
  }

  protected getDropType(nftCollection: Nftcollection): 'series' | 'oneofone' | null {
    return nftCollection.dropType || null;
  }

  protected getPublishType(nftCollection: Nftcollection): 'hosted' | 'partner' | null {
    return nftCollection.publishType || null;
  }

  protected getPhases(nftCollection: Nftcollection): Nftcollection['phases'] {
    return nftCollection.phases;
  }

  protected getMarketUrl(nftCollection: Nftcollection): string | undefined {
    return nftCollection.marketUrl || undefined;
  }

  protected getMarketname(nftCollection: Nftcollection): string | undefined {
    return nftCollection.requiredInformation?.datamarket || undefined;
  }

  protected getSymbol(nftCollection: Nftcollection): string | undefined {
    return nftCollection.requiredInformation?.blockchain?.toUpperCase() || undefined;
  }

  protected getIsDrop(nftCollection: Nftcollection): boolean {
    const status = this.getStatus(nftCollection);
    return status === 'upcoming';
  }

  protected getTags(nftCollection: Nftcollection): string[] {
    return combineTags(
      this.getIsDrop(nftCollection) ? 'UPCOMING' : null,
      getBlockchainTag(nftCollection.requiredInformation?.blockchain),
      getTokenStandardTag(nftCollection.tokenStandard),
      getYearTag(nftCollection.createdDate, nftCollection.startDate, nftCollection.createdAt),
      getMarketplaceTag(nftCollection.requiredInformation?.datamarket),
      getCategoryTags(nftCollection.artCategory)
    );
  }

  protected getCategories(nftCollection: Nftcollection): string[] {
    const categories: string[] = [];
    if (nftCollection.artCategory && Array.isArray(nftCollection.artCategory)) {
      nftCollection.artCategory.forEach(category => {
        if (typeof category === 'number') {
          categories.push(String(category));
        } else if (category && typeof category === 'object' && 'title' in category) {
          categories.push(category.title);
        }
      });
    }
    return categories;
  }

  protected getStatus(nftCollection: Nftcollection): string {
    return nftCollection.status || '';
  }

  /**
   * Enrich NFT collection with computed display fields
   * Used by getBySlug to add tags, categories, and drop status
   */
  enrichCollection(nftCollection: Nftcollection): EnrichedCollection {
    return {
      ...nftCollection,
      tags: this.getTags(nftCollection),
      cats: this.getCategories(nftCollection),
      collectionTypes: nftCollection.collectionTypes || [],
      dropTypes: nftCollection.dropTypes || [],
      isDrop: this.getIsDrop(nftCollection),
    };
  }

  // Override toCatalogItem to add additional fields
  toCatalogItem(nftCollection: Nftcollection) {
    const catalogItem = super.toCatalogItem(nftCollection);
    const isDrop = this.getIsDrop(nftCollection);

    // Reset additional data after use
    const artworkCount = this.artworkCount;
    const collectorCount = this.collectorCount;
    this.artworkCount = undefined;
    this.collectorCount = undefined;

    return {
      ...catalogItem,
      isDrop,
      // Use the provided counts if available
      artworkCount: artworkCount ?? catalogItem.artworkCount,
      collectorCount: collectorCount ?? catalogItem.collectorCount,
    };
  }
}

// Export singleton instance
export const nftCollectionTransformer = new NftCollectionTransformer();